/**
 * Modal Viewport Fix Utility
 *
 * WEB ONLY — keeps legacy behavior for browser layouts where modals could
 * appear off-screen after page scroll.
 *
 * Native Capacitor apps skip this entirely: forcing height:100vh + centered
 * flex on every scroll/resize fights the soft keyboard, bottom sheets, and
 * is a major mobile jank source.
 */

import { Capacitor } from '@capacitor/core';

let initialized = false;
let observerInstance = null;

const isNativeApp = () => {
  try {
    return typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export const fixModalViewportPositioning = () => {
  if (isNativeApp()) return;

  const modalOverlays = document.querySelectorAll('div[class*="fixed"][class*="inset-0"]');

  modalOverlays.forEach((overlay) => {
    // Skip bottom-sheet style overlays (items-end) — don't force center
    const className = typeof overlay.className === 'string' ? overlay.className : '';
    if (className.includes('items-end')) return;

    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.right = '0';
    overlay.style.bottom = '0';
    overlay.style.width = '100vw';
    overlay.style.height = '100vh';
    overlay.style.zIndex = '50';
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.transform = 'none';
    overlay.classList.add('modal-viewport-center');
  });
};

export const observeModalChanges = () => {
  if (isNativeApp()) return null;

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'childList') continue;
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches?.('div[class*="fixed"][class*="inset-0"]')) {
          fixModalViewportPositioning();
          return;
        }
        if (node.querySelectorAll?.('div[class*="fixed"][class*="inset-0"]').length > 0) {
          fixModalViewportPositioning();
          return;
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return observer;
};

export const initializeModalViewportFix = () => {
  if (isNativeApp()) {
    return null;
  }

  if (initialized) {
    return observerInstance;
  }
  initialized = true;

  fixModalViewportPositioning();
  observerInstance = observeModalChanges();

  // Web only: resize is enough — do NOT bind scroll (major jank)
  window.addEventListener('resize', fixModalViewportPositioning);

  return observerInstance;
};

// Auto-init for web only (native no-ops inside initializeModalViewportFix)
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeModalViewportFix);
  } else {
    initializeModalViewportFix();
  }
}
